export interface TypeItem {
    value: string;
    label: string;
}

/** Lower-case words of a label or query; punctuation (such as the parentheses around a code) splits words. */
const words = (text: string): string[] => text.toLowerCase().match(/[a-z0-9]+/g) ?? [];

/** Whether every typed word starts a word of the label (so a code or a name fragment matches), or the label contains the query. */
export function typeMatches(query: string, label: string): boolean {
    const queryWords = words(query);
    if (queryWords.length === 0) return true;
    const labelWords = words(label);
    return (
        label.toLowerCase().includes(query.trim().toLowerCase()) ||
        queryWords.every((typed) => labelWords.some((w) => w.startsWith(typed)))
    );
}

/** Edit distance between two short strings; an insert, delete, substitution or swap of neighbours costs one. */
export function editDistance(a: string, b: string): number {
    const table = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array<number>(b.length).fill(0)]);
    for (let j = 1; j <= b.length; j++) table[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
            table[i][j] = Math.min(
                table[i - 1][j] + 1,
                table[i][j - 1] + 1,
                table[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
            );
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                table[i][j] = Math.min(table[i][j], table[i - 2][j - 2] + 1);
            }
        }
    }
    return table[a.length][b.length];
}

/** Typos allowed in a typed word: none for short words, one up to five letters, two beyond. */
const allowedTypos = (word: string): number => (word.length <= 3 ? 0 : word.length <= 5 ? 1 : 2);

/** Fewest typos between a typed word and one label word, counting the typed word as a possible prefix. */
function wordDistance(typed: string, labelWord: string): number {
    const prefix = editDistance(typed, labelWord.slice(0, typed.length));
    return Math.min(editDistance(typed, labelWord), prefix);
}

/** Total typos for a query against one label, or null when any typed word matches no label word closely enough. */
function labelDistance(queryWords: string[], label: string): number | null {
    const labelWords = words(label);
    let total = 0;
    for (const typed of queryWords) {
        const best = Math.min(...labelWords.map((w) => wordDistance(typed, w)));
        if (best > allowedTypos(typed)) return null;
        total += best;
    }
    return total;
}

/** The item a mistyped query most likely meant (typed words match label words, or a code, within a few typos), or null. */
export function suggestType<T extends TypeItem>(query: string, items: readonly T[]): T | null {
    const queryWords = words(query);
    if (queryWords.length === 0) return null;
    let best: { item: T; distance: number } | null = null;
    for (const item of items) {
        const distance = labelDistance(queryWords, item.label);
        if (distance === null) continue;
        // Ties go to the shorter label: "Service Bulletin" over "Service Bulletin Status Report".
        const better =
            best === null ||
            distance < best.distance ||
            (distance === best.distance && item.label.length < best.item.label.length);
        if (better) best = { item, distance };
    }
    return best?.item ?? null;
}
